import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api, formatDate } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import {
  Star, MapPin, Calendar, Award, Clock, Edit3, LogOut, Settings, ChevronRight,
  Trophy, Medal, Crown, Sparkles, Heart, Users as UsersIcon, X, Plus, Camera
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
import SEO from '@/components/SEO';

const BADGE_ICONS = {
  trophy: Trophy, clock: Clock, medal: Medal, star: Star,
  crown: Crown, sparkles: Sparkles, users: UsersIcon, heart: Heart,
};

export default function Profile() {
  const { userId } = useParams();
  const { user: authUser, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [newSkill, setNewSkill] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const isOwnProfile = !userId || userId === authUser?.user_id;

  useEffect(() => {
    loadProfile();
  }, [userId, authUser]);

  const loadProfile = async () => {
    try {
      const targetId = userId || authUser?.user_id;
      if (!targetId) return;
      const [profileRes, reviewsRes] = await Promise.all([
        api.get(`/profile/${targetId}`),
        api.get(`/reviews/${targetId}`),
      ]);
      setProfile(profileRes.data);
      setReviews(reviewsRes.data);
      if (isOwnProfile) {
        setEditForm({
          name: profileRes.data.name || '',
          bio: profileRes.data.bio || '',
          school: profileRes.data.school || '',
          age: profileRes.data.age || '',
          skills: profileRes.data.skills || [],
          interests: profileRes.data.interests || [],
          availability: profileRes.data.availability || [],
        });
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      await api.patch('/profile', {
        ...editForm,
        age: editForm.age ? parseInt(editForm.age) : null,
      });
      await refreshUser();
      await loadProfile();
      setShowEdit(false);
      toast.success('Profile updated!');
    } catch {
      toast.error('Failed to update');
    }
  };

  const addSkill = () => {
    if (newSkill.trim() && !editForm.skills.includes(newSkill.trim())) {
      setEditForm({ ...editForm, skills: [...editForm.skills, newSkill.trim()] });
      setNewSkill('');
    }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/profile-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshUser();
      await loadProfile();
      toast.success('Profile picture updated!');
    } catch {
      toast.error('Failed to upload picture');
    }
    setUploading(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) return (
    <div className="px-4 pt-6 max-w-lg mx-auto">
      <div className="animate-pulse space-y-4">
        <div className="w-20 h-20 rounded-3xl bg-gray-100 mx-auto" />
        <div className="h-6 bg-gray-100 rounded w-1/2 mx-auto" />
        <div className="h-4 bg-gray-100 rounded w-1/3 mx-auto" />
      </div>
    </div>
  );

  if (!profile) return null;

  const xpProgress = (profile.xp || 0) % 100;
  const p = profile;
  const isOrg = p.role === 'organization';

  return (
    <div className="px-4 pt-6 max-w-lg mx-auto pb-8" data-testid="profile-page">
      <SEO title={`${p.name} — Profile`} description={`${p.name}'s TeenHands profile — volunteer hours, badges, skills, and reviews.`} path="/profile" />
      {/* Header */}
      <div className="text-center mb-6 animate-fade-in-up">
        <div className="relative inline-block">
          {(p.picture_path || p.picture) ? (
            <img
              src={p.picture_path ? `${API_URL}/files/${p.picture_path}` : p.picture}
              alt={p.name}
              className="w-20 h-20 rounded-3xl object-cover mx-auto mb-3 border-4 border-white shadow-lg"
            />
          ) : (
            <div className={`w-20 h-20 rounded-3xl flex items-center justify-center text-white text-3xl font-bold mx-auto mb-3 shadow-lg ${isOrg ? 'bg-[#6C63FF]' : 'bg-[#2EC4B6]'}`}>
              {p.name?.[0]}
            </div>
          )}
          {isOwnProfile && (
            <label
              className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#2EC4B6] rounded-full flex items-center justify-center cursor-pointer shadow-md hover:bg-[#25A296] transition-colors"
              data-testid="upload-picture-btn"
            >
              <Camera size={14} className="text-white" />
              <input type="file" accept="image/*" className="hidden" onChange={handlePictureUpload} disabled={uploading} />
            </label>
          )}
        </div>
        <h1 className="text-2xl font-bold text-[#011627]">{p.name}</h1>
        {isOrg && p.organization_name && (
          <p className="text-sm font-bold text-[#6C63FF]">{p.organization_name}</p>
        )}
        <p className="text-sm text-[#5C6B7F] font-semibold capitalize">{p.role} {!isOrg && p.school && `at ${p.school}`}</p>
        {p.bio && <p className="text-sm text-[#5C6B7F] mt-2 max-w-xs mx-auto">{p.bio}</p>}
      </div>

      {/* Level Progress - Teen only */}
      {!isOrg && (
        <div className="bg-white rounded-2xl p-4 border-2 border-gray-100 mb-4 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold text-[#011627]">Level {p.level || 1}</span>
            <span className="text-xs font-bold text-[#9AA5B1]">{xpProgress}/100 XP to next level</span>
          </div>
          <Progress value={xpProgress} className="h-3 bg-gray-100" />
        </div>
      )}

      {/* Stats */}
      {isOrg ? (
        <div className="mb-4 animate-fade-in-up" style={{ animationDelay: '150ms' }} />
      ) : (
        <div className="grid grid-cols-3 gap-3 mb-4 animate-fade-in-up" style={{ animationDelay: '150ms' }}>
          {[
            { value: p.total_hours?.toFixed(1) || '0', label: 'Vol Hours', color: '#2EC4B6' },
            { value: p.rating_avg?.toFixed(1) || '-', label: 'Rating', color: '#FF9F1C' },
            { value: p.reviews_count || 0, label: 'Reviews', color: '#6C63FF' },
          ].map(({ value, label, color }) => (
            <div key={label} className="bg-white rounded-2xl p-4 text-center border-2 border-gray-100">
              <p className="text-2xl font-bold" style={{ color }}>{value}</p>
              <p className="text-xs font-bold text-[#9AA5B1] uppercase">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Badges - Teen only */}
      {!isOrg && p.badges?.length > 0 && (
        <div className="bg-white rounded-3xl p-5 border-2 border-gray-100 mb-4 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
          <h3 className="font-bold text-[#011627] mb-3 flex items-center gap-2">
            <Award size={18} className="text-[#FF9F1C]" /> Badges
          </h3>
          <div className="grid grid-cols-4 gap-3">
            {p.badges.map(badge => {
              const IconComponent = BADGE_ICONS[badge.icon] || Star;
              return (
                <div key={badge.badge_id} className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-2xl bg-[#FF9F1C]/10 flex items-center justify-center mb-1">
                    <IconComponent size={24} className="text-[#FF9F1C]" />
                  </div>
                  <span className="text-[10px] font-bold text-[#5C6B7F] leading-tight">{badge.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Skills - Teen only */}
      {!isOrg && p.skills?.length > 0 && (
        <div className="bg-white rounded-3xl p-5 border-2 border-gray-100 mb-4 animate-fade-in-up" style={{ animationDelay: '250ms' }}>
          <h3 className="font-bold text-[#011627] mb-3">Skills</h3>
          <div className="flex flex-wrap gap-2">
            {p.skills.map(skill => (
              <span key={skill} className="px-3 py-1 bg-[#6C63FF]/10 text-[#6C63FF] rounded-full text-sm font-bold">{skill}</span>
            ))}
          </div>
        </div>
      )}

      {/* Availability - Teen only */}
      {!isOrg && p.availability?.length > 0 && (
        <div className="bg-white rounded-3xl p-5 border-2 border-gray-100 mb-4">
          <h3 className="font-bold text-[#011627] mb-3 flex items-center gap-2">
            <Calendar size={18} className="text-[#2EC4B6]" /> Availability
          </h3>
          <div className="flex flex-wrap gap-2">
            {p.availability.map(a => (
              <span key={a} className="px-3 py-1 bg-[#2EC4B6]/10 text-[#2EC4B6] rounded-full text-sm font-bold">{a}</span>
            ))}
          </div>
        </div>
      )}

      {/* Reviews - Teen only */}
      {!isOrg && reviews.length > 0 && (
        <div className="bg-white rounded-3xl p-5 border-2 border-gray-100 mb-4 animate-fade-in-up" style={{ animationDelay: '300ms' }}>
          <h3 className="font-bold text-[#011627] mb-3">Reviews</h3>
          <div className="space-y-3">
            {reviews.slice(0, 5).map(r => (
              <div key={r.review_id} className="border-b border-gray-100 pb-3 last:border-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-bold text-sm text-[#011627]">{r.reviewer_name}</span>
                  <div className="flex">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} size={12} className={s <= r.rating ? 'text-[#FF9F1C] fill-[#FF9F1C]' : 'text-gray-300'} />
                    ))}
                  </div>
                </div>
                <p className="text-sm text-[#5C6B7F]">{r.comment}</p>
                <p className="text-[10px] text-[#9AA5B1] mt-1">for {r.opp_title}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      {isOwnProfile && (
        <div className="space-y-2 animate-fade-in-up" style={{ animationDelay: '350ms' }}>
          <Button
            onClick={() => setShowEdit(true)}
            className="w-full rounded-2xl py-5 bg-[#2EC4B6] hover:bg-[#25A296] text-white font-bold btn-bounce"
            data-testid="edit-profile-btn"
          >
            <Edit3 size={18} className="mr-2" /> Edit Profile
          </Button>
          {!isOrg && (
            <Button
              onClick={() => navigate('/hours')}
              variant="outline"
              className="w-full rounded-2xl py-5 border-2 font-bold btn-bounce"
              data-testid="view-hours-btn"
            >
              <Clock size={18} className="mr-2" /> Activity Tracker
            </Button>
          )}
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full rounded-2xl py-5 text-[#E71D36] font-bold"
            data-testid="logout-btn"
          >
            <LogOut size={18} className="mr-2" /> Log Out
          </Button>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="rounded-3xl border-2 border-gray-100 max-w-md max-h-[80vh] overflow-y-auto" data-testid="edit-profile-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Edit Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              placeholder="Name" value={editForm.name}
              onChange={e => setEditForm({ ...editForm, name: e.target.value })}
              className="bg-gray-50 border-2 border-gray-100 rounded-2xl px-4 py-3 font-semibold"
              data-testid="edit-name"
            />
            <Textarea
              placeholder="Bio" value={editForm.bio}
              onChange={e => setEditForm({ ...editForm, bio: e.target.value })}
              className="bg-gray-50 border-2 border-gray-100 rounded-2xl font-semibold"
              data-testid="edit-bio"
            />
            {p.role === 'teen' && (
              <>
                <Input
                  placeholder="School" value={editForm.school}
                  onChange={e => setEditForm({ ...editForm, school: e.target.value })}
                  className="bg-gray-50 border-2 border-gray-100 rounded-2xl px-4 py-3 font-semibold"
                  data-testid="edit-school"
                />
                <Input
                  type="number" placeholder="Age" value={editForm.age} min={13} max={18}
                  onChange={e => setEditForm({ ...editForm, age: e.target.value })}
                  className="bg-gray-50 border-2 border-gray-100 rounded-2xl px-4 py-3 font-semibold"
                  data-testid="edit-age"
                />
              </>
            )}

            {/* Skills - Teen only */}
            {p.role === 'teen' && (
            <div>
              <p className="text-sm font-bold text-[#011627] mb-2">Skills</p>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add skill" value={newSkill}
                  onChange={e => setNewSkill(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  className="bg-gray-50 border-2 border-gray-100 rounded-2xl px-4 py-2 font-semibold text-sm"
                  data-testid="edit-skill-input"
                />
                <Button type="button" size="sm" onClick={addSkill} className="rounded-xl bg-[#6C63FF] text-white">
                  <Plus size={16} />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {editForm.skills?.map(s => (
                  <span key={s} className="px-2 py-1 bg-[#6C63FF]/10 text-[#6C63FF] rounded-full text-xs font-bold flex items-center gap-1">
                    {s} <button onClick={() => setEditForm({ ...editForm, skills: editForm.skills.filter(x => x !== s) })}><X size={12} /></button>
                  </span>
                ))}
              </div>
            </div>
            )}

            {/* Availability - Teen only */}
            {p.role === 'teen' && (
            <div>
              <p className="text-sm font-bold text-[#011627] mb-2">Availability</p>
              <div className="flex flex-wrap gap-2">
                {['Weekdays', 'Weekends', 'After School', 'Mornings', 'Evenings'].map(a => (
                  <button
                    key={a} type="button"
                    onClick={() => {
                      const avail = editForm.availability || [];
                      setEditForm({
                        ...editForm,
                        availability: avail.includes(a) ? avail.filter(x => x !== a) : [...avail, a],
                      });
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${
                      editForm.availability?.includes(a) ? 'border-[#2EC4B6] bg-[#2EC4B6]/10 text-[#2EC4B6]' : 'border-gray-100 text-[#5C6B7F]'
                    }`}
                    data-testid={`avail-${a.toLowerCase().replace(' ', '-')}`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
            )}

            <Button
              onClick={handleSave}
              className="w-full rounded-full py-5 bg-[#2EC4B6] hover:bg-[#25A296] text-white font-bold btn-bounce"
              data-testid="save-profile-btn"
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
